export interface ChatSuggestionDashboardItem {
  user_id: number
  target_group: string
  pharmacist: string
  _created: string
}

export interface ChatSuggetsionDashboard {
  total: number
  page_size: number
  page: number
  has_next_page: boolean
  results: ChatSuggestionDashboardItem[]
}

export interface ChatSuggestion {
  suggestion_id: number
  text: string
  order_number: number
}

export type ChatContentType = 'TEXT' | 'FILE' | 'BUTTON'

export const FLIPPER_WS_EVENT_TYPE = {
  SYSTEM: 0,
  MESSAGE: 1,
  TYPING: 2,
  MSG_RECEIVED: 3
} as const

export const FLIPPER_WS_CONTENT_TYPE = {
  TEXT: 1,
  BUTTON: 2,
  FILE: 3
} as const

export type FlipperWsEventType = keyof typeof FLIPPER_WS_EVENT_TYPE

export type FlipperWsContentType = keyof typeof FLIPPER_WS_CONTENT_TYPE

export interface FlipperWsEventBase {
  id: string
  type: FlipperWsEventType | number | string
  content_type?: FlipperWsContentType | number | string
  value: string | number
  owner_id?: number
  room?: number
  timestamp?: string
  translated?: boolean
  translation_id?: number | null
  translation?: ChatTranslation | null
  document?: ChatDocument | null
  walrus_ai?: boolean
  user_id?: number
  target_user_id?: number
}

export interface ChatDocument {
  file_name: string
  file_size: number
  mime_type: string
}

export interface UploadedDocument {
  documentId: number
  documents: ChatDocument
}

export interface ChatTranslation {
  text: string
  language: string
}

export interface ServerMessage {
  id: string
  type: 'MESSAGE' | 'TYPING' | 'SYSTEM'
  value: string | number
  content_type: ChatContentType
  documents?: ChatDocument
  translation?: ChatTranslation
  owner_id?: number
  user_id?: number
  walrus_ai?: boolean
  timestamp?: string
}

export interface ChatMessage {
  id: string
  content: string
  isSender: boolean
  contentType: ChatContentType
  documents?: ChatDocument
  documentId?: number
  translation?: ChatTranslation
  ownerUserId?: number
  walrus_ai?: boolean
  timestamp?: string
  isPending?: boolean
  sendFailed?: boolean
  errorMessage?: string
}

export interface ChatTranslationSettings {
  on: boolean
  language: string
}

export interface PendingChatMessage {
  id: string
  userId: number
  roomId: number
  ownerId: number
  content: string
  contentType: ChatContentType
  value: string | number
  documents?: ChatDocument
  clientCreated: string
  // times it went out on a socket without Flipper confirming it
  attempts?: number
  // when it last went out on a socket, in ms
  sentAt?: number
}

export interface ChatListMessage {
  value: string
  _created?: string
  created?: string
  timestamp?: string
  owner: number
  owner_name: string
  owner_preferred_name?: string
  type?: string
}

export interface ChatListResultItem {
  user_id: number
  room_id: number
  first_name: string
  last_name: string
  preferred_name?: string
  message?: ChatListMessage | null
  gender?: string
  age?: number
  unread_count?: number
  priority?: 'high' | 'medium' | 'low'
  online?: boolean
}

export interface ChatListResponse {
  results: ChatListResultItem[]
  page: number
  page_size: number
  has_next_page: boolean
  total_events: number
}

export interface ChatListFilters {
  search: string
  category: number
  matched_to_me: boolean
  last_message_direction: '' | 'inbound' | 'outbound' | 'all'
}

export interface ChatListItem {
  userId: number
  roomId: number
  name: string
  preferredName?: string
  subtitle: string | null
  previewText: string
  previewCreated: string
  previewOwnerName: string
  isFilePreview: boolean
  unreadCount?: number
  priority?: 'high' | 'medium' | 'low'
  online: boolean
}

export interface IncomingMessageNotification {
  id: string
  userId: number
  name: string
  preview: string
  roomId: number
  isFilePreview: boolean
}

export interface MemberListState {
  items: ChatListItem[]
  page: number
  hasNextPage: boolean
  isLoading: boolean
  filters: ChatListFilters
  totalEvents: number
  roomIdToUserId: Record<number, number>
}

export interface ChatState {
  isFlipperWSConnected: boolean
  pendingMessagesById: Record<string, PendingChatMessage>
  messagesByUserId: Record<number, ChatMessage[]>
  hasNextPageByUserId: Record<number, boolean>
  pageByUserId: Record<number, number>
  isLoadingMessagesByUserId: Record<number, boolean>
  isOtherTypingByUserId: Record<number, boolean>
  translationByUserId: Record<number, ChatTranslationSettings>
  activeUserId: number | null
  activeProfessionalUserId: number | null
  activeRoomId: number | null
  memberList: MemberListState
  // rooms found by searching the list for members it has not loaded
  memberRoomIds: Record<number, number>
  incomingMessageNotifications: IncomingMessageNotification[]

  setFlipperWSConnected: (isConnected: boolean) => void
  shouldConnectFlipperWS: () => boolean
  setActiveUserId: (
    userId: number | null,
    professionalUserId?: number | null,
    roomId?: number | null
  ) => void
  addPendingMessage: (message: PendingChatMessage) => void
  removePendingMessageById: (messageId: string) => void
  failPendingMessageById: (messageId: string, errorMessage: string) => void
  retrySendPendingMessages: () => Promise<void>
  connectSocket: () => void
  disconnectSocket: () => void
  fetchMessages: (
    userId: number,
    page?: number,
    pageSize?: number,
    language?: string,
    professionalUserId?: number
  ) => Promise<void>
  markAsRead: (userId: number) => Promise<boolean>
  setTranslation: (
    userId: number,
    on: boolean,
    language: string
  ) => Promise<boolean>
  saveLanguageSetting: (userId: number, language: string) => Promise<boolean>
  uploadDocument: (
    userId: number,
    file: File
  ) => Promise<UploadedDocument | null>
  getDocuments: (userId: number) => Promise<unknown[] | null>
  getDocumentUrl: (userId: number, documentId: number) => Promise<string | null>
  // returns the id of the message it queued, to follow its delivery
  sendTextMessage: (
    userId: number,
    text: string,
    context: { roomId: number; ownerId: number }
  ) => string | undefined
  sendFileMessage: (
    userId: number,
    documentId: number,
    context: { roomId: number; ownerId: number },
    documents?: ChatDocument
  ) => void
  sendTyping: (userId: number, roomId: number, value: 'start' | 'stop') => void
  getChatSuggestionsDashboard: (
    page: number,
    page_size: number
  ) => Promise<ChatSuggetsionDashboard | null>
  getChatSuggestionsByUserId: (
    userId: number
  ) => Promise<ChatSuggestion[] | null>
  sendChatSuggestion: (
    userId: number,
    suggestionId: number,
    revision?: string
  ) => Promise<boolean>
  rejectChatSuggestion: (
    userId: number,
    suggestionId: number
  ) => Promise<boolean>
  fetchMemberList: (options?: {
    reset?: boolean
    professionalUserId?: number | null
  }) => Promise<void>
  loadMoreMemberList: (options?: {
    professionalUserId?: number | null
  }) => Promise<void>
  setMemberListFilters: (
    partial: Partial<ChatListFilters>,
    options?: { professionalUserId?: number | null }
  ) => Promise<void>
  upsertMemberListFromWs: (event: FlipperWsEventBase) => void
  pushIncomingMessageNotification: (
    notification: Omit<IncomingMessageNotification, 'id'>
  ) => void
  removeIncomingMessageNotification: (id: string) => void
  clearIncomingMessageNotifications: () => void
  getMemberListRoomId: (userId: number) => number | undefined
  fetchMemberRoomId: (userId: number) => Promise<number | undefined>
  clear: () => void
}
